// Shared per-account credential control: Connect / Rotate / Test / Revoke a
// venue API key against the manager-reachable endpoints
//   GET/POST/DELETE /ops/accounts/{id}/credentials
//   POST            /ops/accounts/{id}/credentials/test
// Extracted from Settings.tsx (slice 14.a.4c.3) so the fund-onboarding wizard's
// Step 2 (14.a.4c.4) can reuse it for a fund's accounts. Behaviour is identical
// to the original Settings ConnectionRow — it is parameterized only by the
// account it acts on + an onChange callback fired after a mutation.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';

import { api } from '../lib/api';

export interface CredentialAccount {
  id: string;
  label: string;
  venue: string;
  has_credentials: boolean;
  key_last_four: string | null;
  credential_status: string | null;
}

export function CredentialRow({ account, onChange }: {
  account: CredentialAccount; onChange: () => void;
}) {
  const [expand, setExpand] = useState(false);
  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');
  const [passphrase, setPassphrase] = useState('');
  // A Kraken key is a PAIR — no passphrase. The SERVER has known this since
  // K3a (portfolio/views.py: `if account.venue != Account.VENUE_KRAKEN`, which
  // stores a '-' sentinel), but this form did not: it rendered the field
  // unconditionally and disabled Save until it was filled, so Kraken
  // credentials could never be entered AT ALL — the venue was selectable and
  // its key was unusable. Mirror the server's rule rather than restating it
  // loosely: exactly one venue is exempt.
  const needsPassphrase = account.venue !== 'kraken';
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [testOk, setTestOk] = useState<boolean | null>(null);

  const onTest = async () => {
    setTesting(true); setTestMsg(null); setTestOk(null);
    try {
      const r = await api<{ ok: boolean; row_count?: number; nonzero_count?: number;
                            latency_ms?: number; error_kind?: string; detail?: string }>(
        `/ops/accounts/${account.id}/credentials/test`, { method: 'POST' },
      );
      setTestOk(r.ok);
      if (r.ok) setTestMsg(`OK · ${r.row_count} accounts · ${r.nonzero_count} with balance · ${r.latency_ms}ms`);
      else setTestMsg(`${(r.error_kind || 'failed').toUpperCase()}: ${r.detail || ''}`);
    } catch (e: any) {
      setTestOk(false);
      setTestMsg(`Request failed: ${e?.payload?.detail || ''}`);
    } finally { setTesting(false); }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setMsg(null); setErr(null);
    try {
      await api(`/ops/accounts/${account.id}/credentials`, {
        method: 'POST',
        body: needsPassphrase
          ? { key: key.trim(), secret: secret.trim(), passphrase: passphrase.trim() }
          : { key: key.trim(), secret: secret.trim() },
      });
      setKey(''); setSecret(''); setPassphrase('');
      setMsg('Saved. Snapshot will use this key on the next refresh.');
      setExpand(false);
      onChange();
    } catch (e: any) {
      const detail = e?.payload?.detail;
      setErr(typeof detail === 'string' ? detail : 'Failed to save credentials.');
    } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!window.confirm(`Revoke ${account.label || 'this Coinbase Exchange'} key? Next snapshot will fall back to env-based credentials (or skip).`)) return;
    setErr(null); setMsg(null);
    try {
      await api(`/ops/accounts/${account.id}/credentials`, { method: 'DELETE' });
      setMsg('Revoked.');
      onChange();
    } catch { setErr('Failed to revoke.'); }
  };

  const status = account.has_credentials
    ? `CONNECTED · key ****${account.key_last_four}` + (account.credential_status === 'needs_reauth' ? ' · NEEDS REAUTH' : '')
    : 'NOT CONNECTED';

  return (
    <div style={{ padding: '18px 0', borderBottom: 'var(--hairline)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr auto', gap: 28, alignItems: 'center' }}>
        <span className="label-s">{(account.label || 'COINBASE').toUpperCase()}</span>
        <div>
          <span className="label-s" style={{
            color: account.has_credentials
              ? (account.credential_status === 'needs_reauth' ? 'var(--accent)' : 'var(--ink-2)')
              : 'var(--ink-3)',
          }}>
            {status}
          </span>
          <span className="serif-body" style={{ display: 'block', fontSize: 13, marginTop: 6, color: 'var(--ink-3)' }}>
            View-only API keys only. Withdrawal permission must be disabled.
          </span>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {account.has_credentials && (
            <button className="btn-link" onClick={onTest} disabled={testing}>
              {testing ? 'Testing…' : 'Test'}
            </button>
          )}
          <button className="btn-link" onClick={() => setExpand(v => !v)}>
            {expand ? 'Cancel' : (account.has_credentials ? 'Rotate' : 'Connect')}
          </button>
          {account.has_credentials && (
            <button className="btn-link" onClick={revoke}>Revoke</button>
          )}
        </div>
      </div>
      {expand && (
        <form onSubmit={save} style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 520 }}>
          <CredField label="API KEY" value={key} onChange={setKey} />
          <CredField label="API SECRET (BASE64)" value={secret} onChange={setSecret} />
          {needsPassphrase && (
            <CredField label="PASSPHRASE" value={passphrase} onChange={setPassphrase} />
          )}
          {err && <Note>{err}</Note>}
          <div>
            <button type="submit" className="btn"
                    disabled={saving || !key || !secret || (needsPassphrase && !passphrase)}>
              {saving ? 'Saving…' : 'Save'} <span className="btn-arrow">→</span>
            </button>
          </div>
        </form>
      )}
      {msg && <Note>{msg}</Note>}
      {testMsg && (
        <p className="serif-body" style={{
          margin: '8px 0 0', fontSize: 13,
          color: testOk ? 'var(--accent)' : 'var(--oxblood, #8a3030)',
        }}>
          {testMsg}
        </p>
      )}
    </div>
  );
}

function CredField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="label-s" style={{ display: 'block', marginBottom: 6 }}>{label}</label>
      <input
        type="password" autoComplete="off" required
        value={value} onChange={e => onChange(e.target.value)}
        className="field-input"
        style={{ width: '100%' }}
      />
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <p className="serif-body" style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--accent)' }}>{children}</p>;
}
